import React, { Children } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { range, ceil } from 'lodash';
import {
	compose,
	withProps,
	defaultProps,
	withStateHandlers,
	withHandlers,
} from 'recompose';

import { Text, Paragraph } from 'components/core/Typography';

import StarButton from './StarButton';

import './StarInput.scss';

export const enhance = compose(
	defaultProps({ totalStars: 5 }),
	withProps((props) => {
		const numElements = props.halfStep
			? props.totalStars * 2
			: props.totalStars;
		return {
			stars: range(1, numElements + 1).map((index) =>
				props.halfStep ? index / 2 : index,
			),
		};
	}),
	withHandlers({
		onSelect: (props) => (value) => props.input.onChange(`${value}`),
	}),
	withStateHandlers(
		(props) => ({ activeRating: parseFloat(props.input.value) }),
		{
			onMouseEnter: () => (value) => ({ activeRating: value }),
			onMouseLeave: (state, props) => () => ({
				activeRating: parseFloat(props.input.value),
			}),
		},
	),
	withProps(({ activeRating, helpTexts }) => {
		const index = ceil(activeRating - 1);
		return {
			helpText:
				helpTexts && index < helpTexts.length
					? `${helpTexts[index]} ${activeRating}`
					: null,
		};
	}),
);

export function StarInput({
	activeRating,
	className,
	halfStep,
	stars,
	onMouseEnter,
	onMouseLeave,
	onSelect,
	size,
	helpText,
	label,
	error,
}) {
	return (
		<div className={classnames('star-input', size, className)}>
			<span
				className={classnames(
					'ss-label ss-star-input-label',
					className,
					{ error },
				)}
			>
				<Text className="ss-label-text">{label}</Text>
				<div className="input-container">
					{Children.toArray(
						stars.map((value) => (
							<StarButton
								className={classnames(size, {
									half: halfStep,
									active: activeRating >= value,
								})}
								onMouseEnter={onMouseEnter}
								onMouseLeave={onMouseLeave}
								onClick={onSelect}
								value={value}
							/>
						)),
					)}
					{!!helpText && <div className="help-text">{helpText}</div>}
				</div>
				{error && (
					<Paragraph className="error-message">{error}</Paragraph>
				)}
			</span>
		</div>
	);
}

StarInput.propTypes = {
	activeRating: PropTypes.number,
	className: PropTypes.string,
	halfStep: PropTypes.bool,
	stars: PropTypes.arrayOf(PropTypes.number),
	onMouseEnter: PropTypes.func,
	onMouseLeave: PropTypes.func,
	onSelect: PropTypes.func,
	size: PropTypes.string,
	helpText: PropTypes.string,
	label: PropTypes.string,
	error: PropTypes.string,
};

export default enhance(StarInput);
